import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAllAvailableTools,
  getAvailableTools,
  getBotSystemPrompt,
  getBotTemperature,
  getBotTools,
  updateBotSystemPrompt,
  updateBotTemperature,
  updateBotTools,
} from '@/store/chat/actions';

import {
  selectBotSettingsErrors,
  selectBotSettingsLoading,
  selectBotSystemPrompt,
  selectBotTemperature,
  selectBotTools,
  selectSelectedBotId,
} from '@/store/chat/selectors';

import { Button, Card, Form, Input, message, Modal, Slider, Spin, Tabs, Typography } from 'antd';
import BotSelector from '@/components/BotSelector';
import { EditOutlined, FireOutlined, ReloadOutlined, SaveOutlined, ToolOutlined } from '@ant-design/icons';

import PropTypes from 'prop-types';

import styles from './BotSettingsModal.module.scss';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const BotSettingsModal = ({ open, onCancel, botId: propBotId }) => {
  const dispatch = useDispatch();
  const [localSystemPrompt, setLocalSystemPrompt] = useState('');
  const [localBotTools, setLocalBotTools] = useState({}); // Now it's an object
  const [localTemperature, setLocalTemperature] = useState(0.7);
  const [activeTab, setActiveTab] = useState('prompt');

  // Selectors
  const systemPrompt = useSelector(selectBotSystemPrompt);
  const botTools = useSelector(selectBotTools);
  const botTemperature = useSelector(selectBotTemperature);
  const loading = useSelector(selectBotSettingsLoading);
  const errors = useSelector(selectBotSettingsErrors);
  const selectedBotId = useSelector(selectSelectedBotId);

  // Use selected bot from store or fallback to prop
  const botId = selectedBotId || propBotId;

  // Sync store data with local state
  useEffect(() => {
    setLocalSystemPrompt(systemPrompt);
  }, [systemPrompt]);

  useEffect(() => {
    setLocalBotTools({ ...botTools });
  }, [botTools]);

  useEffect(() => {
    setLocalTemperature(botTemperature);
  }, [botTemperature]);

  useEffect(() => {
    if (open && botId && botId !== '') {
      loadBotData();
    }
  }, [open, botId]);

  // Обработка ошибок
  useEffect(() => {
    if (errors.getBotSystemPrompt) {
      message.error('Не удалось загрузить системный промпт');
    }
    if (errors.updateBotSystemPrompt) {
      message.error('Ошибка при сохранении системного промпта');
    }
    if (errors.getBotTools) {
      message.error('Не удалось загрузить инструменты бота');
    }
    if (errors.updateBotTools) {
      message.error('Ошибка при сохранении инструментов');
    }
    if (errors.getBotTemperature) {
      message.error('Не удалось загрузить температуру бота');
    }
    if (errors.updateBotTemperature) {
      message.error('Ошибка при сохранении температуры');
    }
    if (errors.getAvailableTools) {
      message.warning('Не удалось загрузить доступные инструменты для бота, пробуем загрузить общий список');
      // Пробуем загрузить общий список всех инструментов как fallback
      dispatch(getAllAvailableTools());
    }
  }, [errors]);

  const loadBotData = () => {
    // Загружаем данные через Redux store
    dispatch(getBotSystemPrompt(botId));
    dispatch(getBotTools(botId));
    dispatch(getBotTemperature(botId));

    // Пробуем загрузить доступные инструменты для конкретного бота
    // Если не получится, загрузим общий список
    dispatch(getAvailableTools(botId));
  };

  // Перезагрузка системного промпта
  const reloadSystemPrompt = () => {
    dispatch(getBotSystemPrompt(botId));
  };

  // Перезагрузка инструментов
  const reloadBotTools = () => {
    dispatch(getBotTools(botId));
  };

  // Перезагрузка температуры
  const reloadTemperature = () => {
    dispatch(getBotTemperature(botId));
  };

  const saveSystemPrompt = () => {
    dispatch(
      updateBotSystemPrompt(botId, localSystemPrompt, () => {
        message.success('Системный промпт успешно обновлен');
      })
    );
  };

  const saveBotTools = () => {
    dispatch(
      updateBotTools(botId, localBotTools, () => {
        message.success('Инструменты успешно обновлены');
      })
    );
  };

  const saveTemperature = () => {
    dispatch(
      updateBotTemperature(botId, localTemperature, () => {
        message.success('Температура успешно обновлена');
      })
    );
  };

  // Функция для получения описания температуры
  const getTemperatureDescription = temp => {
    if (temp < 0.3) return 'Очень консервативный';
    if (temp < 0.7) return 'Консервативный';
    if (temp < 1.0) return 'Сбалансированный';
    if (temp < 1.5) return 'Креативный';
    return 'Очень креативный';
  };

  const handleToolUpdate = (toolName, description) => {
    setLocalBotTools(prev => ({
      ...prev,
      [toolName]: description,
    }));
  };

  // const addTool = toolName => {
  //   const availableTool = availableTools.find(tool => tool.name === toolName);
  //   if (availableTool && !localBotTools[toolName]) {
  //     setLocalBotTools(prev => ({
  //       ...prev,
  //       [toolName]: availableTool.description,
  //     }));
  //   }
  // };

  const renderSystemPromptTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.promptSection}>
        <Title level={4}>
          <EditOutlined /> Системный промпт
        </Title>
        <Text type='secondary'>Определите поведение и личность чат-бота</Text>
        <TextArea
          value={localSystemPrompt}
          onChange={e => setLocalSystemPrompt(e.target.value)}
          placeholder='Введите системный промпт для чат-бота...'
          rows={12}
          className={styles.promptTextArea}
        />
        <div className={styles.actionButtons}>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            onClick={saveSystemPrompt}
            loading={loading.updateBotSystemPrompt}
          >
            Сохранить промпт
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reloadSystemPrompt} disabled={loading.getBotSystemPrompt}>
            Перезагрузить
          </Button>
        </div>
      </div>
    </div>
  );

  const renderToolsTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.toolsSection}>
        <Title level={4}>
          <ToolOutlined /> Инструменты бота
        </Title>
        <Text type='secondary'>Управление инструментами и их описаниями</Text>

        {/* Временно отключено добавление инструментов */}
        {/* <div className={styles.addToolSection}>
          <Select
            placeholder="Добавить инструмент"
            style={{ width: 300, marginBottom: 16 }}
            onSelect={addTool}
            value={undefined}
          >
            {availableTools
              .filter(tool => !localBotTools[tool.name])
              .map(tool => (
                <Option key={tool.name} value={tool.name}>
                  {tool.name}
                </Option>
              ))}
          </Select>
        </div> */}

        <div className={styles.toolsList}>
          {/* Временно отключено удаление инструментов (убран параметр extra с кнопкой удаления) */}
          {Object.entries(localBotTools).map(([toolName, toolDescription]) => (
            <Card key={toolName} className={styles.toolCard} title={toolName}>
              <Form.Item label='Описание'>
                <TextArea
                  value={toolDescription || ''}
                  onChange={e => handleToolUpdate(toolName, e.target.value)}
                  placeholder='Описание инструмента...'
                  rows={4}
                />
              </Form.Item>
            </Card>
          ))}
        </div>

        <div className={styles.actionButtons}>
          <Button type='primary' icon={<SaveOutlined />} onClick={saveBotTools} loading={loading.updateBotTools}>
            Сохранить инструменты
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reloadBotTools} disabled={loading.getBotTools}>
            Перезагрузить
          </Button>
        </div>
      </div>
    </div>
  );

  const renderTemperatureTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.temperatureSection}>
        <Title level={4}>Настройка температуры</Title>
        <Text type='secondary' className={styles.description}>
          Температура контролирует креативность ответов бота. Низкие значения делают ответы более предсказуемыми,
          высокие - более творческими и разнообразными. Диапазон: 0.0 - 2.0
        </Text>

        <div className={styles.temperatureControl}>
          <div className={styles.temperatureHeader}>
            <Title level={5}>Текущее значение</Title>
            <Text className={styles.temperatureValue}>
              {localTemperature} - {getTemperatureDescription(localTemperature)}
            </Text>
          </div>

          <Slider
            min={0}
            max={2}
            step={0.05}
            value={localTemperature}
            onChange={setLocalTemperature}
            marks={{
              0: '0',
              0.5: '0.5',
              1: '1',
              1.5: '1.5',
              2: '2',
            }}
            className={styles.temperatureSlider}
          />

          <div className={styles.temperatureLabels}>
            <Text type='secondary'>Предсказуемый</Text>
            <Text type='secondary'>Креативный</Text>
          </div>
        </div>

        <div className={styles.actionButtons}>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            onClick={saveTemperature}
            loading={loading.updateBotTemperature}
            disabled={localTemperature === botTemperature}
          >
            Сохранить температуру
          </Button>
          <Button icon={<ReloadOutlined />} onClick={reloadTemperature} disabled={loading.getBotTemperature}>
            Перезагрузить
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title='Настройки чат-бота'
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      className={styles.botSettingsModal}
    >
      <Spin
        spinning={
          loading.getBotSystemPrompt || loading.getBotTools || loading.getAvailableTools || loading.getBotTemperature
        }
      >
        <div className={styles.botSelectorSection}>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            Выберите бота для настройки:
          </Typography.Title>
          <BotSelector placeholder='Выберите бота для настройки' size='large' />
        </div>

        {botId && botId !== '' && (
          <Tabs activeKey={activeTab} onChange={setActiveTab} size='large'>
            <TabPane
              tab={
                <span>
                  <EditOutlined />
                  Системный промпт
                </span>
              }
              key='prompt'
            >
              {renderSystemPromptTab()}
            </TabPane>
            <TabPane
              tab={
                <span>
                  <ToolOutlined />
                  Инструменты
                </span>
              }
              key='tools'
            >
              {renderToolsTab()}
            </TabPane>
            <TabPane
              tab={
                <span>
                  <FireOutlined />
                  Температура
                </span>
              }
              key='temperature'
            >
              {renderTemperatureTab()}
            </TabPane>
          </Tabs>
        )}

        {(!botId || botId === '') && (
          <div className={styles.noBotSelected}>
            <Typography.Text type='secondary'>
              Выберите бота из списка выше для настройки его параметров
            </Typography.Text>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

BotSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  botId: PropTypes.string,
};

export default BotSettingsModal;
